import { getAdjacentHexagons } from './index';

const wouldCreateGap = (q, r, selectedHexagons) => {
	const coordKey = `${q},${r}`;
	
	// If there are 2 or fewer hexagons selected, removing one can't create a gap
	if (selectedHexagons.size <= 2) {
		return false;
	}
	
	// Create a copy without the hexagon we want to remove
	const withoutHexagon = new window.Map(selectedHexagons);
	withoutHexagon.delete(coordKey);
	
	// Check if remaining hexagons are still connected using flood fill
	const remaining = Array.from(withoutHexagon.keys());
	if (remaining.length === 0) return false;
	
	// Start from the first remaining hexagon and see if we can reach all others
	const visited = new Set();
	const queue = [remaining[0]];
	visited.add(remaining[0]);
	
	while (queue.length > 0) {
		const current = queue.shift();
		const [currentQ, currentR] = current.split(',').map(Number);
		
		// Check all adjacent hexagons
		const adjacents = getAdjacentHexagons(currentQ, currentR);
		for (const { q: aq, r: ar } of adjacents) {
			const adjacentKey = `${aq},${ar}`;
			if (withoutHexagon.has(adjacentKey) && !visited.has(adjacentKey)) {
				visited.add(adjacentKey);
				queue.push(adjacentKey);
			}
		}
	}
	
	// If we couldn't visit all remaining hexagons, there would be a gap
	return visited.size !== remaining.length;
};

export const toggleHexagonSelection = (q, r, centerCoordinate, selectedHexagons, setSelectedHexagons) => {
	const coordKey = `${q},${r}`;
	
	setSelectedHexagons(prev => {
		const newSelection = new window.Map(prev);
		
		if (newSelection.has(coordKey)) {
			// Check if removing this hexagon would create a gap
			if (wouldCreateGap(q, r, newSelection)) {
				// Don't allow deselection if it would create a gap
				return prev;
			}
			newSelection.delete(coordKey);
		} else {
			// Check if this hexagon is adjacent to any selected hexagon
			if (newSelection.size === 0) {
				newSelection.set(coordKey, { q, r, centerCoordinate });
			} else {
				// Check adjacency
				const adjacentHexagons = getAdjacentHexagons(q, r);
				const isAdjacent = adjacentHexagons.some(({ q: aq, r: ar }) => 
					newSelection.has(`${aq},${ar}`)
				);
				
				if (isAdjacent) {
					newSelection.set(coordKey, { q, r, centerCoordinate });
				}
			}
		}
		
		return newSelection;
	});
};

export const updateHexagonVisualState = (mapObject, selectedHexagons) => {
	if (!mapObject || !mapObject.getSource('hexagon-grid')) return;
	
	const source = mapObject.getSource('hexagon-grid');
	const data = source._data;
	
	if (data && data.features) {
		data.features.forEach(feature => {
			const coordKey = `${feature.properties.q},${feature.properties.r}`;
			feature.properties.selected = selectedHexagons.has(coordKey);
		});
		
		source.setData(data);
	}
};

export const createHexagonClickHandler = (mapObject, toggleHexagonSelectionCallback) => {
	return (e) => {
		if (e.features.length > 0) {
			const feature = e.features[0];
			const coordinates = feature.geometry.coordinates[0];
			// Calculate center coordinate from polygon vertices
			const centerLng = coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length;
			const centerLat = coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length;
			const centerCoordinate = [centerLng, centerLat];
			toggleHexagonSelectionCallback(feature.properties.q, feature.properties.r, centerCoordinate);
		}
	};
};